import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, Image, Link, Text, useTranslation, useUi } from '@weareconceptstudio/core';

//* HOC's
import { withDataContext } from '@/context';

//* Libs
import { getLatestRelatedRecipes, getRecipesRelatedProduct } from '@/libs/get-data';

//* Helpers
import { config } from '@/helpers';

//* Styles
import CookingStyle from './style';

//* Components
import Button from '@/components/Button';
import StyledQuotedText from '@/components/StyledQuotedText';
import TitleAnim from '@/modules/_animation/Title';

gsap.registerPlugin(ScrollTrigger);

const Cooking = ({ id, globalData, isHome = false }) => {
	const { selectedLang } = useTranslation();
	const { winWidth } = useUi();

	const { title, description } = globalData.recipe;

	//! States
	const [data, setData] = useState([]);

	//! Ref
	const wrapperItemRef = useRef();
	const scrollRef = useRef();
	const styleRef = useRef();
	const buttonRef = useRef();
	const descriptionRef = useRef();
	const titleRef = useRef();

	//! Data
	useEffect(() => {
		const fetchData = async () => {
			let dataFetch = [];

			if (isHome) {
				dataFetch = await getLatestRelatedRecipes({ lang: selectedLang });
			} else {
				dataFetch = await getRecipesRelatedProduct({ id, lang: selectedLang });
			}

			setData(dataFetch);
		};

		fetchData();
	}, [id, selectedLang, isHome]);

	useEffect(() => {
		if (styleRef.current) {
			const children = [...wrapperItemRef.current.children];

			if (!descriptionRef.current.style.opacity) {
				if (descriptionRef.current) {
					gsap.set(descriptionRef.current, { opacity: 0 });
				}

				if (buttonRef.current) {
					gsap.set(buttonRef.current, { scale: 0 });
				}

				if (winWidth < 1280 && winWidth > 768) {
					gsap.set([...children], { clearProps: 'all' });

					gsap.set(children[0], { x: '50%', y: '15%', rotate: -10 });
					gsap.set(children[1], { position: 'relative', zIndex: 10 });
					gsap.set(children[2], { x: '-50%', y: '15%', rotate: 10 });
					gsap.set(children[3], { x: '85%', y: '-15%', rotate: -10 });
					gsap.set(children[4], { x: '-85%', y: '-15%', rotate: 10, position: 'relative', zIndex: 9 });
				} else if (winWidth <= 768) {
					gsap.set([...children], { x: '0', y: '100%', opacity: 0 });
				} else {
					gsap.set([...children], { clearProps: 'all' });

					gsap.set(children[0], { x: '150%', rotate: -20 });
					gsap.set(children[1], { x: '75%', rotate: -10, y: '-20%' });
					gsap.set(children[3], { x: '-75%', rotate: 10, y: '-20%' });
					gsap.set(children[4], { x: '-150%', rotate: 20 });
				}
			}
			scrollRef.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top bottom',
				trigger: titleRef.current,
				// markers: true,
				onEnter: () => {
					if (descriptionRef.current) {
						gsap.to(descriptionRef.current, { opacity: 1, duration: 0.6, ease: 'power2.in', delay: 0.4 });
					}
					if (buttonRef.current) {
						gsap.to(buttonRef.current, { scale: 1, duration: 0.6, ease: 'power2.out', delay: 0.5 });
					}

					if (winWidth <= 768) {
						gsap.to([...children], { x: 0, y: 0, rotate: 0, duration: 1, ease: 'power2.out', delay: 0.1, stagger: 0.06 });
						gsap.to([...children], { opacity: 1, duration: 0.5, ease: 'power2.in', delay: 0.1, stagger: 0.06 });
					} else {
						gsap.to(children[0], { x: 0, y: 0, rotate: 0, duration: 1, ease: 'power2.out', delay: 0.1 });
						gsap.to(children[1], { x: 0, y: 0, rotate: 0, duration: 1, ease: 'power2.out', delay: 0.1 });
						gsap.to(children[2], { x: 0, y: 0, rotate: 0, duration: 1, ease: 'power2.out', delay: 0.1 });
						gsap.to(children[3], { x: 0, y: 0, rotate: 0, duration: 1, ease: 'power2.out', delay: 0.1 });
						gsap.to(children[4], { x: 0, y: 0, rotate: 0, duration: 1, ease: 'power2.out', delay: 0.1 });
					}
				},
				onLeaveBack: () => {
					if (descriptionRef.current) {
						gsap.killTweensOf(descriptionRef.current);
						gsap.set(descriptionRef.current, { opacity: 0 });
					}

					if (buttonRef.current) {
						gsap.killTweensOf(buttonRef.current);
						gsap.set(buttonRef.current, { scale: 0 });
					}

					gsap.killTweensOf(children);
					if (winWidth < 1280 && winWidth > 768) {
						gsap.set([...children], { clearProps: 'all' });

						gsap.set(children[0], { x: '50%', y: '15%', rotate: -10 });
						gsap.set(children[1], { position: 'relative', zIndex: 10 });
						gsap.set(children[2], { x: '-50%', y: '15%', rotate: 10 });
						gsap.set(children[3], { x: '85%', y: '-15%', rotate: -10 });
						gsap.set(children[4], { x: '-85%', y: '-15%', rotate: 10, position: 'relative', zIndex: 9 });
					} else if (winWidth <= 768) {
						gsap.set([...children], { y: '100%', opacity: 0 });
					} else {
						gsap.set([...children], { clearProps: 'all' });

						gsap.set(children[0], { x: '150%', rotate: -20 });
						gsap.set(children[1], { x: '75%', rotate: -10, y: '-20%' });
						gsap.set(children[3], { x: '-75%', rotate: 10, y: '-20%' });
						gsap.set(children[4], { x: '-150%', rotate: 20 });
					}
				},
			});
		}
		return () => {
			if (scrollRef.current) {
				scrollRef.current.kill();
			}
		};
	});

	return data?.length > 0 ? (
		<CookingStyle ref={styleRef}>
			<Container row>
				<div className='empty-col col-3 col-dx-2 col-m-12' />

				<div className='cooking-wrapper col-6 col-dx-8 col-m-12'>
					<div
						ref={titleRef}
						className='cooking-inner-wrapper'>
						<TitleAnim
							trigger={'bottom'}
							load={true}>
							<StyledQuotedText text={title} />
						</TitleAnim>
						<Text
							ref={descriptionRef}
							className='p1 font-fss-light white-1000 cooking-description opacity-zero'>
							{description}
						</Text>
						<div
							ref={buttonRef}
							className='button-wrap-link'>
							<Button
								btnType={'yellow-type'}
								text={'allRecipes'}
								url={config.routes.recipes.path}
								className={'capitalize cooking-btn'}
							/>
						</div>
					</div>
				</div>

				<div className='empty-col col-3 col-dx-2 col-m-12' />
			</Container>

			<Container className='cooking-product-related-wrapper'>
				<div
					ref={wrapperItemRef}
					className='cooking-product-related-inner-wrapper'>
					{data.slice(0, 5).map((item, index) => {
						return (
							<div
								key={index}
								className={`related-product-wrapper item-${index}`}>
								<Link
									url={`${config.routes.recipes.path}${item.slug}`}
									className='related-product-link-wrapper'>
									<div className='related-product-inner-wrapper'>
										<div className='related-product-image-wrapper'>
											<Image
												src={item.image.src}
												alt={item.image.alt}
											/>
										</div>

										<div className='related-product-info-wrapper'>
											<div className='related-product-info-inner-wrapper'>
												<Text className='p2 green-1000 font-fss-regular date-text'>{item.tag || 'TAG'}</Text>

												<Text className='p1 green-1000 font-larken-regular banner-text'>{item.title}</Text>
											</div>
										</div>
									</div>
								</Link>
							</div>
						);
					})}
				</div>
			</Container>
		</CookingStyle>
	) : null;
};

export default withDataContext(Cooking, ['globalData']);
